import React from 'react';
import { Award, Target, Shield, Users } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              26 Years of Waterproofing Excellence
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Built on a foundation of trust, expertise, and proven results across thousands of projects.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Our Journey
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Founded over two decades ago, roofsnwalls began with a simple mission: deliver waterproofing solutions that actually last. What started as a small team handling residential repairs has grown into a trusted name in both residential and government infrastructure projects.
                </p>
                <p>
                  Our founder brought decades of hands-on construction experience to create a company focused on workmanship, quality materials, and genuine accountability. Unlike temporary fixes, we believe in understanding the root cause and delivering solutions built for the long term.
                </p>
                <p>
                  Today, we've successfully waterproofed over 10 million square feet across 2,000+ homes and multiple government facilities in Jabalpur. Our transition from small projects to large-scale infrastructure work reflects our commitment to continuous improvement and technical excellence.
                </p>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_leakdefense-pros/artifacts/ztjjtoaq_ChatGPT%20Image%20Mar%2029%2C%202026%20at%2001_20_46%20PM.png"
                alt="roofsnwalls Team"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              What Drives Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our core principles that guide every project we undertake.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Experience</h3>
              <p className="text-sm text-slate-600">
                26 years of field execution across diverse projects and conditions.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Precision</h3>
              <p className="text-sm text-slate-600">
                Attention to detail in diagnosis, material selection, and application.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Durability</h3>
              <p className="text-sm text-slate-600">
                Focus on long-lasting solutions over temporary quick fixes.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Accountability</h3>
              <p className="text-sm text-slate-600">
                Genuine warranties and standing behind our work for years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Expert Workforce
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Our team consists of trained professionals with years of field experience. From initial assessment to final inspection, every member understands that quality waterproofing requires skill, patience, and proper technique.
            </p>
            <p className="text-base text-slate-600 leading-relaxed">
              We invest in continuous training, ensuring our workforce stays updated with the latest materials and methods while maintaining the proven techniques that have served us for decades.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

const Building = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);